/* Otervano LAB R3: one list, trailing expand/collapse control and lifecycle-safe rating.
   No network requests and no changes to variants, prices, cart or checkout. */
(() => {
  'use strict';
  const tag = 'ov-eyewear-reviews';
  if (customElements.get(tag)) return;
  const slotSelector = '[data-ovew-rating-slot]';
  const relevantSelector = `.ov-buy-block--heading, product-info, ${slotSelector}`;


  // Keep one explicit notice in the preview chrome, outside all review cards
  // and product content. The Liquid renderer also supplies it without JavaScript.
  function syncPreviewStatus() {
    const notices = Array.from(document.querySelectorAll('#OvReviewPreviewStatus'));
    const styles = Array.from(document.querySelectorAll('#OvReviewPreviewStatusStyle'));
    const active = Array.from(document.querySelectorAll(`${tag}[data-internal="true"]`))
      .filter((element) => element.isConnected);
    if (!active.length) {
      [...notices, ...styles].forEach((element) => element.remove());
      return;
    }
    const source = active.map((element) => element.querySelector('template[data-ovr-preview-status-template]'))
      .find(Boolean);
    if (!source) return;
    let notice = notices.shift();
    let style = styles.shift();
    [...notices, ...styles].forEach((element) => element.remove());
    if (!style) {
      style = source.content.querySelector('#OvReviewPreviewStatusStyle')?.cloneNode(true);
      if (style) document.head.append(style);
    }
    if (!notice) notice = source.content.querySelector('#OvReviewPreviewStatus')?.cloneNode(true);
    if (notice && (notice.parentElement !== document.body || document.body.firstElementChild !== notice)) {
      document.body.prepend(notice);
    }
  }

  class EyewearReviews extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const signal = this.abort.signal;
      queueMicrotask(syncPreviewStatus);
      this.root = this.closest('.ov-lab-page') || document.getElementById('MainContent');
      this.grid = this.querySelector('[data-ovr-grid]');
      this.toggle = this.querySelector('[data-ovr-toggle]');
      this.status = this.querySelector('[data-ovr-status]');
      this.cards = Array.from(this.querySelectorAll('[data-review-block]'));
      this.extraCards = this.cards.filter((card) => card.hasAttribute('data-review-extra'));

      if (this.grid && this.toggle && this.extraCards.length) {
        this.setExpanded(false);
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => {
          this.setExpanded(this.toggle.getAttribute('aria-expanded') !== 'true', {userAction: true});
        }, {signal});
      }

      this.queueSummary = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          if (this.isConnected) this.mountSummary();
        });
      };
      this.addEventListener('shopify:block:select', (event) => {
        const id = event.detail?.blockId;
        const card = this.cards.find((item) => item.dataset.reviewBlock === id);
        if (!card) return;
        if (card.hidden) this.setExpanded(true);
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          if (card.isConnected) card.scrollIntoView({block: 'nearest', inline: 'center', behavior: 'auto'});
        });
      }, {signal});
      document.addEventListener('click', (event) => {
        if (window.Shopify?.inspectMode) return;
        const link = event.target instanceof Element ? event.target.closest('[data-ovew-review-jump]') : null;
        if (!link || !this.root?.contains(link)) return;
        event.preventDefault();
        this.scrollIntoView({
          block: 'start',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
        this.querySelector('h2')?.focus({preventScroll: true});
      }, {signal});
      for (const name of ['shopify:section:load', 'variant:change', 'product-info:loaded']) {
        document.addEventListener(name, this.queueSummary, {signal});
      }
      if (this.root) {
        // Rating-internal updates do not trigger another rating replacement.
        this.observer = new MutationObserver((records) => {
          const relevant = records.some((record) =>
            [...record.addedNodes, ...record.removedNodes].some((node) =>
              node instanceof Element && (node.matches(relevantSelector) || node.querySelector(relevantSelector))
            )
          );
          if (relevant) this.queueSummary();
        });
        this.observer.observe(this.root, {childList: true, subtree: true});
        this.mountSummary();
      }
    }

    setExpanded(expanded, {userAction = false} = {}) {
      if (!this.grid || !this.toggle) return;
      // Extra cards stay in the same list; there is never a second scroll container.
      this.extraCards.forEach((card) => { card.hidden = !expanded; });
      this.toggle.setAttribute('aria-expanded', String(expanded));
      this.dataset.expanded = String(expanded);
      if (userAction && this.status) {
        const visible = expanded ? this.cards.length : this.cards.length - this.extraCards.length;
        this.status.textContent = `${visible} de ${this.cards.length} reseñas disponibles.`;
      }
      cancelAnimationFrame(this.scrollFrame);
      if (!expanded) this.grid.scrollTo({left: 0, behavior: 'instant'});
      if (!userAction) return;
      this.scrollFrame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        if (expanded && mobile && this.extraCards[0]) {
          const firstNew = this.extraCards[0];
          const padding = parseFloat(getComputedStyle(this.grid).paddingLeft) || 0;
          const left = this.grid.scrollLeft + firstNew.getBoundingClientRect().left
            - this.grid.getBoundingClientRect().left - padding;
          this.grid.scrollTo({
            left: Math.max(0, left),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
          });
        } else if (!expanded) {
          // Collapsing a tall desktop grid must not leave the user below the control.
          const rect = this.toggle.getBoundingClientRect();
          if (rect.top < 0 || rect.bottom > window.innerHeight) {
            this.toggle.scrollIntoView({block: 'nearest', behavior: 'instant'});
          }
        }
      });
    }

    mountSummary() {
      if (!this.root) return;
      const source = this.querySelector('template[data-ovew-rating]');
      let slot = this.root.querySelector(slotSelector);
      if (!source) {
        if (slot?.dataset.ovReviewSection === this.dataset.sectionId) slot.remove();
        return;
      }
      const heading = this.root.querySelector('.ov-buy-block--heading');
      if (!heading) return;
      if (!slot) {
        slot = document.createElement('div');
        slot.dataset.ovewRatingSlot = '';
      }
      if (heading.nextElementSibling !== slot) heading.after(slot);
      slot.dataset.ovReviewSection = this.dataset.sectionId || '';
      slot.hidden = false;
      if (slot.innerHTML.trim() !== source.innerHTML.trim()) {
        slot.replaceChildren(source.content.cloneNode(true));
      }
    }

    disconnectedCallback() {
      queueMicrotask(syncPreviewStatus);
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.scrollFrame);
      const slot = this.root?.querySelector(slotSelector);
      if (slot?.dataset.ovReviewSection === this.dataset.sectionId) slot.remove();
    }
  }
  customElements.define(tag, EyewearReviews);
})();
